import { FiEdit3, FiInbox, FiSearch, FiUserCheck, FiPackage, FiTool, FiCheckCircle, FiThumbsUp, FiTruck, FiLock, FiPause, FiXCircle, FiClock, FiSend, FiCheck, FiX, FiAlertTriangle, FiShield, FiRefreshCw, FiMinus, FiArrowUp, FiAlertCircle, FiFileText } from 'react-icons/fi';
import Riyal from '../components/ui/Riyal';

// Single source of truth for every status badge in the app.
// `tone` is resolved by <StatusBadge>/<Badge> into light+dark Tailwind classes —
// never hardcode status colors in pages.

export const JOB_STATUS = {
  draft: { tone: 'orange', i18nKey: 'status.draft', icon: FiEdit3 },
  received: { tone: 'blue', i18nKey: 'status.received', icon: FiInbox },
  diagnosis: { tone: 'yellow', i18nKey: 'status.diagnosis', icon: FiSearch },
  pending_customer_approval: { tone: 'amber', i18nKey: 'status.pending_customer_approval', icon: FiUserCheck },
  waiting_parts: { tone: 'orange', i18nKey: 'status.waiting_parts', icon: FiPackage },
  in_progress: { tone: 'purple', i18nKey: 'status.in_progress', icon: FiTool },
  quality_check: { tone: 'indigo', i18nKey: 'status.quality_check', icon: FiShield },
  ready: { tone: 'green', i18nKey: 'status.ready', icon: FiThumbsUp },
  pending_payment: { tone: 'pink', i18nKey: 'status.pending_payment', icon: Riyal },
  awaiting_payment: { tone: 'pink', i18nKey: 'status.awaiting_payment', icon: FiClock },
  delivered: { tone: 'teal', i18nKey: 'status.delivered', icon: FiTruck },
  delivered_pending_payment: { tone: 'pink', i18nKey: 'status.delivered_pending_payment', icon: FiTruck },
  closed: { tone: 'slate', i18nKey: 'status.closed', icon: FiLock },
  on_hold: { tone: 'gray', i18nKey: 'status.on_hold', icon: FiPause },
  cancelled: { tone: 'red', i18nKey: 'status.cancelled', icon: FiXCircle },
};

export const TASK_STATUS = {
  pending: { tone: 'yellow', i18nKey: 'status.pending', icon: FiClock },
  in_progress: { tone: 'purple', i18nKey: 'status.in_progress', icon: FiTool },
  completed: { tone: 'green', i18nKey: 'status.completed', icon: FiCheckCircle },
  approved: { tone: 'green', i18nKey: 'status.approved', icon: FiCheck },
  rejected: { tone: 'red', i18nKey: 'status.rejected', icon: FiX },
  partially_approved: { tone: 'amber', i18nKey: 'status.partially_approved', icon: FiAlertTriangle },
  qc_passed: { tone: 'green', i18nKey: 'status.qc_passed', icon: FiShield },
  qc_failed: { tone: 'red', i18nKey: 'status.qc_failed', icon: FiShield },
  under_qc: { tone: 'indigo', i18nKey: 'status.under_qc', icon: FiShield },
  on_hold: { tone: 'gray', i18nKey: 'status.on_hold', icon: FiPause },
  blocked: { tone: 'red', i18nKey: 'status.on_hold', icon: FiPause },
  // Display only: a concern whose work staff cancelled (stored 'rejected').
  cancelled: { tone: 'gray', i18nKey: 'status.cancelled', icon: FiXCircle },
};

export const INVOICE_STATUS = {
  draft: { tone: 'gray', i18nKey: 'status.draft', icon: FiEdit3 },
  sent: { tone: 'blue', i18nKey: 'status.sent', icon: FiSend },
  paid: { tone: 'green', i18nKey: 'status.paid', icon: FiCheckCircle },
  partially_paid: { tone: 'amber', i18nKey: 'status.partially_paid', icon: Riyal },
  partial: { tone: 'amber', i18nKey: 'status.partially_paid', icon: Riyal },
  unpaid: { tone: 'orange', i18nKey: 'status.unpaid', icon: FiClock },
  pending: { tone: 'yellow', i18nKey: 'status.pending', icon: FiClock },
  overdue: { tone: 'red', i18nKey: 'status.overdue', icon: FiAlertCircle },
  cancelled: { tone: 'red', i18nKey: 'status.cancelled', icon: FiXCircle },
};

export const QUOTATION_STATUS = {
  draft: { tone: 'gray', i18nKey: 'status.draft', icon: FiEdit3 },
  sent: { tone: 'blue', i18nKey: 'status.sent', icon: FiSend },
  pending: { tone: 'yellow', i18nKey: 'status.pending', icon: FiClock },
  // Receipt-model quotation stuck awaiting its downpayment receipt — shares
  // the same i18n key + tone as job_cards' pending_payment (JOB_STATUS above).
  pending_payment: { tone: 'pink', i18nKey: 'status.pending_payment', icon: Riyal },
  approved: { tone: 'green', i18nKey: 'status.approved', icon: FiCheck },
  partially_approved: { tone: 'amber', i18nKey: 'status.partially_approved', icon: FiAlertTriangle },
  rejected: { tone: 'red', i18nKey: 'status.rejected', icon: FiX },
  expired: { tone: 'gray', i18nKey: 'status.expired', icon: FiClock },
  converted: { tone: 'teal', i18nKey: 'status.converted', icon: FiRefreshCw },
  // Closed quotation (close instead of delete, 2026-10-19) — «ملغي».
  cancelled: { tone: 'red', i18nKey: 'status.cancelled', icon: FiXCircle },
};

export const PRIORITY = {
  low: { tone: 'gray', i18nKey: 'priority.low', icon: FiMinus },
  normal: { tone: 'blue', i18nKey: 'priority.normal', icon: FiMinus },
  medium: { tone: 'yellow', i18nKey: 'priority.medium', icon: FiArrowUp },
  high: { tone: 'orange', i18nKey: 'priority.high', icon: FiArrowUp },
  urgent: { tone: 'red', i18nKey: 'priority.urgent', icon: FiAlertCircle },
};

export const APPOINTMENT_STATUS = {
  pending: { tone: 'amber', i18nKey: 'appt.status.pending', icon: FiClock },
  confirmed: { tone: 'blue', i18nKey: 'appt.status.confirmed', icon: FiCheck },
  checked_in: { tone: 'accent', i18nKey: 'appt.status.checked_in', icon: FiUserCheck },
  converted: { tone: 'green', i18nKey: 'appt.status.converted', icon: FiRefreshCw },
  cancelled: { tone: 'gray', i18nKey: 'appt.status.cancelled', icon: FiXCircle, strike: true },
  no_show: { tone: 'red', i18nKey: 'appt.status.no_show', icon: FiAlertTriangle },
};

export const CLAIM_STATUS = {
  intake: { tone: 'gray', i18nKey: 'claimStatus.intake', icon: FiInbox },
  report_attached: { tone: 'blue', i18nKey: 'claimStatus.report_attached', icon: FiFileText },
  estimate_received: { tone: 'cyan', i18nKey: 'claimStatus.estimate_received', icon: FiCheckCircle },
  submitted_to_insurer: { tone: 'indigo', i18nKey: 'claimStatus.submitted_to_insurer', icon: FiSend },
  approved: { tone: 'green', i18nKey: 'claimStatus.approved', icon: FiCheck },
  partial: { tone: 'amber', i18nKey: 'claimStatus.partial', icon: FiAlertTriangle },
  rejected: { tone: 'red', i18nKey: 'claimStatus.rejected', icon: FiX },
  surveyor_inspected: { tone: 'purple', i18nKey: 'claimStatus.surveyor_inspected', icon: FiSearch },
  in_repair: { tone: 'orange', i18nKey: 'claimStatus.in_repair', icon: FiTool },
  supplementary_approval: { tone: 'yellow', i18nKey: 'claimStatus.supplementary_approval', icon: FiClock },
  supplementary_approved: { tone: 'green', i18nKey: 'claimStatus.supplementary_approved', icon: FiCheck },
  completed: { tone: 'teal', i18nKey: 'claimStatus.completed', icon: FiCheckCircle },
  surveyor_verified: { tone: 'blue', i18nKey: 'claimStatus.surveyor_verified', icon: FiShield },
  billed_to_insurer: { tone: 'pink', i18nKey: 'claimStatus.billed_to_insurer', icon: Riyal },
  settled: { tone: 'green', i18nKey: 'claimStatus.settled', icon: FiCheckCircle },
};

export const SUBSCRIPTION_STATUS = {
  active: { tone: 'green', i18nKey: 'active', icon: FiCheckCircle },
  pending_approval: { tone: 'amber', i18nKey: 'sa.pendingApproval', icon: FiClock },
  expired: { tone: 'red', i18nKey: 'sa.expired', icon: FiXCircle },
  suspended: { tone: 'orange', i18nKey: 'sa.suspended', icon: FiPause },
};

// Tenant Data Export (Settings → Data Export). Mirrors data_exports.status
// (backend/models/DataExport.js): awaiting_otp -> pending -> running -> ready,
// plus expired/failed/deleting.
export const DATA_EXPORT_STATUS = {
  awaiting_otp: { tone: 'amber', i18nKey: 'de.status.awaiting_otp', icon: FiClock },
  pending: { tone: 'yellow', i18nKey: 'de.status.pending', icon: FiClock },
  running: { tone: 'purple', i18nKey: 'de.status.running', icon: FiRefreshCw },
  ready: { tone: 'green', i18nKey: 'de.status.ready', icon: FiCheckCircle },
  expired: { tone: 'gray', i18nKey: 'de.status.expired', icon: FiXCircle },
  failed: { tone: 'red', i18nKey: 'de.status.failed', icon: FiAlertTriangle },
  deleting: { tone: 'gray', i18nKey: 'de.status.deleting', icon: FiRefreshCw },
};

export const STATUS_TYPES = {
  job: JOB_STATUS,
  task: TASK_STATUS,
  invoice: INVOICE_STATUS,
  quotation: QUOTATION_STATUS,
  priority: PRIORITY,
  appointment: APPOINTMENT_STATUS,
  claim: CLAIM_STATUS,
  subscription: SUBSCRIPTION_STATUS,
  dataExport: DATA_EXPORT_STATUS,
};
